import React, { useEffect, useState } from "react";
import titleImage from "../../assets/JeffopolyDeal.png";
import { AboutModal } from "./AboutModal";
import { useNearbyGames } from "../../utilities/NativeInbound";
import { isNativeHost } from "../../utilities/NativeHost";
import "./StartPage.css";

/** Public TestFlight invite for the iOS beta. */
const TESTFLIGHT_URL = "https://testflight.apple.com/join/JGt389zR";

interface StartPageProps {
    onJoinGame: (gameCode: string, playerName: string) => void;
    /** Device name suggested by a native shell, if we are running inside one. */
    playerNameHint?: string;
}

export function StartPage({ onJoinGame, playerNameHint }: StartPageProps) {
    const [mode, setMode] = useState<"menu" | "create" | "join">("menu");
    const [playerName, setPlayerName] = useState(playerNameHint ?? "");
    const nearbyGames = useNearbyGames();
    const [gameCode, setGameCode] = useState("");
    const [showAbout, setShowAbout] = useState(false);

    // Auto-switch to join mode if ?join=CODE is in the URL
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const joinCode = params.get("join");
        if (joinCode) {
            setGameCode(joinCode.toUpperCase());
            setMode("join");
            // Clean up the URL
            window.history.replaceState({}, "", window.location.pathname);
        }
    }, []);

    const handleCreate = () => {
        if (!playerName.trim()) return;
        // Pass empty game code — GamePage will call CreateGame via SignalR
        onJoinGame("", playerName.trim());
    };

    const handleJoin = () => {
        if (!playerName.trim() || !gameCode.trim()) return;
        onJoinGame(gameCode.trim().toUpperCase(), playerName.trim());
    };

    return (
        <div className="startPage">
            <div className="startPageContent">
                <img src={titleImage} alt="Jeffopoly Deal" className="titleImage" />

                <div className="startPageForm">
                    {mode === "menu" && (
                        <div className="menuButtons">
                            <button className="primary menuButton" onClick={() => setMode("create")}>
                                Create Game
                            </button>
                            <button className="secondary menuButton" onClick={() => setMode("join")}>
                                Join Game
                            </button>
                        </div>
                    )}

                    {mode === "create" && (
                        <div className="formSection">
                            <input
                                type="text"
                                placeholder="Your Name"
                                value={playerName}
                                onChange={(e) => setPlayerName(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                                autoFocus
                                maxLength={20}
                                autoComplete="off"
                                data-1p-ignore
                                data-lpignore="true"
                            />
                            <button className="primary" onClick={handleCreate} disabled={!playerName.trim()}>
                                Create Game
                            </button>
                            <button className="secondary" onClick={() => setMode("menu")}>
                                Back
                            </button>
                        </div>
                    )}

                    {mode === "join" && (
                        <div className="formSection">
                            <input
                                type="text"
                                placeholder="Your Name"
                                value={playerName}
                                onChange={(e) => setPlayerName(e.target.value)}
                                autoFocus
                                maxLength={20}
                                autoComplete="off"
                                data-1p-ignore
                                data-lpignore="true"
                            />
                            <input
                                type="text"
                                placeholder="Game Code"
                                value={gameCode}
                                onChange={(e) => setGameCode(e.target.value.toUpperCase())}
                                onKeyDown={(e) => e.key === "Enter" && handleJoin()}
                                maxLength={4}
                                style={{ textTransform: "uppercase", letterSpacing: "0.2em" }}
                                autoComplete="off"
                                data-1p-ignore
                                data-lpignore="true"
                            />
                            <button className="primary" onClick={handleJoin} disabled={!playerName.trim() || !gameCode.trim()}>
                                Join Game
                            </button>

                            {nearbyGames.length > 0 && (
                                <div className="nearbyGames">
                                    <div className="nearbyGamesLabel">Nearby Games</div>
                                    {nearbyGames.map((game) => (
                                        <button
                                            key={game.gameCode}
                                            className="secondary nearbyGame"
                                            onClick={() => setGameCode(game.gameCode)}
                                        >
                                            <span>{game.hostName}&rsquo;s Game</span>
                                            <span className="nearbyGameCode">{game.gameCode}</span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            <button className="secondary" onClick={() => setMode("menu")}>
                                Back
                            </button>
                        </div>
                    )}
                </div>
            </div>
            {/* Pointless inside the app itself, which is where this link leads. */}
            {!isNativeHost() && (
                <a className="iosDownloadLink" href={TESTFLIGHT_URL} target="_blank" rel="noopener noreferrer">
                    <span className="iosDownloadTitle">Get the iOS app</span>
                    <span className="iosDownloadNote">TestFlight beta</span>
                </a>
            )}
            <button className="aboutLink" onClick={() => setShowAbout(true)}>About Jeffopoly Deal</button>
            <div className="copyrightFooter">
                <p>© {new Date().getFullYear()} Jeff Steinbok. All rights reserved.</p>
                <p>Monopoly Deal is a trademark of Hasbro, Inc.</p>
            </div>
            {showAbout && <AboutModal onClose={() => setShowAbout(false)} />}
        </div>
    );
}
